import { MoreHorizontal, Users } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Group } from "@/lib/api";
import { formatDate } from "@/lib/format";

interface GroupTableProps {
  groups: Group[];
  onManageMembers: (group: Group) => void;
  onRename: (group: Group) => void;
  onDelete: (group: Group) => void;
}

export function GroupTable({ groups, onManageMembers, onRename, onDelete }: GroupTableProps) {
  if (groups.length === 0) {
    return <div className="text-muted-foreground py-12 text-center text-sm">No groups found.</div>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Group</TableHead>
          <TableHead>Members</TableHead>
          <TableHead>Created</TableHead>
          <TableHead className="w-10" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {groups.map((group) => (
          <TableRow
            key={group.id}
            className="cursor-pointer"
            onClick={() => onManageMembers(group)}
          >
            <TableCell>
              <p className="text-foreground text-sm font-medium">{group.name}</p>
            </TableCell>
            <TableCell>
              <Badge variant="secondary" className="gap-1">
                <Users className="size-3" />
                {group.member_count}
              </Badge>
            </TableCell>
            <TableCell className="text-muted-foreground text-sm">
              {formatDate(group.created_at)}
            </TableCell>
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="size-4" />
                    <span className="sr-only">Actions</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  onClick={(e) => e.stopPropagation()}
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  <DropdownMenuItem onClick={() => onManageMembers(group)}>
                    Manage members
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onRename(group)}>Rename group</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onClick={() => onDelete(group)}>
                    Delete group
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
